import React from 'react';

import { fetchCommissionStructure } from '../affiliates/api';
import type { CommissionPlan } from '../affiliates/types';
import { planInForce } from './commission-estimate';

export type CommissionPlans = {
  /** By affiliate id: the plan in force for the brand, null for none. An id whose read failed is absent. */
  plans: ReadonlyMap<string, CommissionPlan | null>;
  pending: boolean;
};

/** The plans read for one set of affiliates (`key`), so a render for another set knows they aren't read yet. */
type PlansState = CommissionPlans & { key: string };

const NONE: PlansState = { key: '', plans: new Map(), pending: false };

/** The last plan read per brand + affiliate this session: shown at once on the next visit while that visit re-reads
 *  it (only good answers are kept). */
const cache = new Map<string, CommissionPlan | null>();
const cacheKey = (casinoInstanceId: string, brandId: string | undefined, affiliateId: string): string =>
  `${casinoInstanceId}|${brandId ?? ''}|${affiliateId}`;

/**
 * The commission plan each of the given affiliates is on for the brand, from the affiliates platform (one read per
 * affiliate: it has no list of plans by affiliate). Re-read every time it is asked for again (the Players page asks
 * for the opened player's affiliate, so on each sheet open), because a plan is assigned or changed on the Affiliates
 * page in the same session; the last answer shows meanwhile, and a failed re-read keeps it. No affiliates asked for,
 * no reads.
 */
export const useCommissionPlans = (
  casinoInstanceId: string | undefined,
  brandId: string | undefined,
  affiliateIds: readonly string[],
): CommissionPlans => {
  const idsKey = React.useMemo(() => [...new Set(affiliateIds)].sort().join(','), [affiliateIds]);
  const [state, setState] = React.useState<PlansState>(NONE);

  // Syncs with the affiliates platform: each affiliate's plan, on every visit.
  React.useEffect(() => {
    if (!casinoInstanceId || idsKey === '') {
      setState(NONE);
      return undefined;
    }
    const ids = idsKey.split(',');
    const held = new Map<string, CommissionPlan | null>();
    ids.forEach((id) => {
      const key = cacheKey(casinoInstanceId, brandId, id);
      if (cache.has(key)) held.set(id, cache.get(key) ?? null);
    });
    let live = true;
    // Pending only while some affiliate has no answer at all yet; one with a held answer shows it while re-read.
    setState({ key: idsKey, plans: new Map(held), pending: held.size < ids.length });
    void Promise.allSettled(
      ids.map(async (id) => {
        const plan = planInForce(await fetchCommissionStructure(casinoInstanceId, id, brandId), brandId);
        cache.set(cacheKey(casinoInstanceId, brandId, id), plan);
        held.set(id, plan);
      }),
    ).then(() => {
      if (live) setState({ key: idsKey, plans: new Map(held), pending: false });
    });
    return () => {
      live = false;
    };
  }, [casinoInstanceId, brandId, idsKey]);

  // Until the effect has run for these affiliates, they are being read, not unknown: no flash of "–" between the
  // render that asks for them and the effect that starts the read.
  return { plans: state.plans, pending: state.pending || (idsKey !== '' && state.key !== idsKey) };
};

export default useCommissionPlans;
